"use client";

import Link from "next/link";
import { useRouter } from "next/navigation";
import { useCallback, useEffect, useState } from "react";

import { JobProgress } from "@/components/jobs/job-progress";
import { useShell } from "@/components/shell/shell-context";
import { ApiError, api, loginUrl } from "@/lib/api/client";
import type { MonitoringStarted, MonitoringStatus, Project } from "@/lib/api/types";
import { formatCount } from "@/lib/format";
import { ALERT_RULES, accessSeconds, headerLine, kpis } from "@/lib/monitoring";

import { BotMatrix } from "./bot-matrix";
import { RobotsCard } from "./robots-card";

const TONE: Record<string, string> = {
  good: "text-success",
  warn: "text-warning",
  bad: "text-danger",
  muted: "text-muted",
};

export function MonitoringView() {
  const { project } = useShell();
  // Bei Projektwechsel neu aufbauen: Status, Rückfrage und Versionsliste gehören zum Projekt
  return project ? <View key={project.id} project={project} /> : null;
}

function View({ project }: { project: Project }) {
  const router = useRouter();
  const [status, setStatus] = useState<MonitoringStatus | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [actionError, setActionError] = useState<string | null>(null);
  const [asking, setAsking] = useState(false);
  const [busy, setBusy] = useState(false);
  const [reload, setReload] = useState(0);

  const load = useCallback(
    () =>
      api<MonitoringStatus>(`/api/monitoring/status?project_id=${project.id}`)
        .then((next) => {
          setStatus(next);
          setError(null);
        })
        .catch((caught: unknown) => {
          if (caught instanceof ApiError && caught.status === 401) {
            router.replace(loginUrl("/monitoring"));
            return;
          }
          setError(caught instanceof Error ? caught.message : "Unbekannter Fehler");
        }),
    [project.id, router],
  );

  useEffect(() => {
    void load();
  }, [load, reload]);

  const running = status?.last_check?.state === "running";
  const mode = status?.last_check?.mode;

  const start = async (checkMode: "robots" | "full") => {
    setBusy(true);
    setActionError(null);
    try {
      await api<MonitoringStarted>("/api/monitoring/checks", {
        method: "POST",
        json: { project_id: project.id, mode: checkMode },
      });
      setAsking(false);
      setReload((current) => current + 1);
    } catch (caught) {
      setActionError(caught instanceof Error ? caught.message : "Unbekannter Fehler");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="mx-auto flex max-w-6xl flex-col gap-6 px-6 py-7 sm:px-9">
      <header className="flex flex-wrap items-end justify-between gap-4">
        <div className="flex flex-col gap-1">
          <div className="text-[13px] text-muted">
            Technisches Monitoring · {status ? headerLine(status) : "Status wird geladen …"}
          </div>
          <h1 className="text-3xl font-semibold tracking-tight">Monitoring</h1>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <button
            type="button"
            disabled
            title="Der Logfile-Import kommt mit S18"
            className="h-11 rounded-md border border-field bg-white px-3.5 text-sm text-muted"
          >
            Logfiles importieren (folgt mit S18)
          </button>
          <button
            type="button"
            onClick={() => setAsking((current) => !current)}
            disabled={running || status === null}
            aria-expanded={asking}
            className="h-11 rounded-md border border-field bg-white px-3.5 text-sm disabled:text-muted"
          >
            Zugriffs-Check starten
          </button>
          <button
            type="button"
            onClick={() => void start("robots")}
            disabled={busy || running || status === null}
            className="h-11 rounded-md bg-accent px-4 text-sm font-medium text-white disabled:opacity-60"
          >
            {running && mode === "robots" ? "Prüfe robots.txt …" : "Jetzt prüfen"}
          </button>
        </div>
      </header>

      {error && (
        <div role="alert" className="flex flex-wrap items-center gap-3 rounded-md bg-danger-bg px-4 py-3 text-sm text-danger">
          <span>{error}</span>
          <button
            type="button"
            onClick={() => setReload((current) => current + 1)}
            className="h-11 rounded-md border border-danger bg-white px-3.5"
          >
            Erneut versuchen
          </button>
        </div>
      )}
      {actionError && (
        <p role="alert" className="text-sm text-danger">
          {actionError}
        </p>
      )}

      {asking && status && (
        <div
          role="group"
          aria-label="Zugriffs-Check bestätigen"
          className="flex flex-wrap items-center gap-3 rounded-md border border-line bg-white px-4 py-3 text-sm"
        >
          <span className="min-w-[280px] flex-1">
            <strong className="font-medium">Zugriffs-Check starten?</strong> Es gehen {formatCount(status.requests_per_full_check)}{" "}
            Requests (je Bot und URL, einer pro {status.request_gap_seconds.toLocaleString("de-DE")} Sekunde, ca.{" "}
            {accessSeconds(status.requests_per_full_check, status.request_gap_seconds)} Sekunden) mit den
            User-Agents der Bots (u. a. Googlebot) an {status.project_domain}. Kostenlos, nur Abrufe der eigenen Domain.
            Ein Request mit gefälschtem User-Agent kommt nicht von der Bot-IP, maßgeblich sind die Logfiles.
          </span>
          <button
            type="button"
            onClick={() => void start("full")}
            disabled={busy}
            className="h-11 rounded-md bg-accent px-3.5 text-[13px] font-medium text-white disabled:opacity-60"
          >
            Zugriffs-Check starten
          </button>
          <button
            type="button"
            onClick={() => setAsking(false)}
            className="h-11 rounded-md border border-field bg-white px-3.5 text-[13px]"
          >
            Abbrechen
          </button>
        </div>
      )}

      {running && status?.last_check?.job_id && (
        <JobProgress
          jobId={status.last_check.job_id}
          title={mode === "full" ? "Zugriffs-Check läuft" : "robots.txt wird geprüft"}
          onFinished={() => setReload((current) => current + 1)}
        />
      )}
      {status?.last_check?.state === "failed" && (
        <p role="alert" className="rounded-md bg-danger-bg px-4 py-3 text-sm text-danger">
          Der letzte Lauf ist fehlgeschlagen{status.last_check.error ? `: ${status.last_check.error}` : "."}
        </p>
      )}

      {status === null && !error && <p className="text-sm text-muted">Monitoring wird geladen …</p>}
      {status && (
        <>
          <section aria-label="Status" className="grid grid-cols-[repeat(auto-fit,minmax(200px,1fr))] gap-4">
            {kpis(status).map((kpi) => (
              <div key={kpi.label} className="flex flex-col gap-1.5 rounded-lg border border-line bg-white px-5 py-4">
                <span className="text-[13px] text-muted">{kpi.label}</span>
                <span className="text-[26px] font-semibold tracking-tight">{kpi.value}</span>
                <span className={`text-xs ${TONE[kpi.tone]}`}>{kpi.detail}</span>
              </div>
            ))}
          </section>

          <div className="flex flex-wrap items-start gap-5">
            <RobotsCard key={status.version?.id ?? "none"} status={status} projectId={project.id} />
            <section aria-labelledby="hits-title" className="flex min-w-0 flex-[1_1_380px] flex-col gap-2 rounded-lg border border-line bg-white px-5 py-[18px]">
              <h2 id="hits-title" className="text-base font-semibold">
                AI-Bot-Hits · 14 Tage, verifiziert
              </h2>
              <p className="text-sm text-muted">
                Das Diagramm kommt mit dem Logfile-Import (S18): Hits je Bot und Tag nach Training, Suchindex und
                Nutzer-Abruf, verifiziert per Reverse-DNS oder IP-Liste der Anbieter. Gespoofte Bots werden getrennt
                ausgewiesen.
              </p>
              <Link href="/settings/connections" className="inline-flex min-h-11 items-center text-[13px] text-accent underline">
                Logfile-Quelle in den Verbindungen prüfen
              </Link>
            </section>
          </div>

          <BotMatrix bots={status.bots} checkedAt={status.access_checked_at} />
          {status.browser && status.browser.ok < status.browser.tested && (
            <p role="status" className="rounded-md bg-warning-bg px-4 py-2.5 text-sm text-warning">
              Auch der Vergleichsabruf mit einem normalen Browser wurde nicht durchgelassen ({status.browser.label}
              {status.browser.waf ? `, ${status.browser.waf}` : ""}). Die Ergebnisse der Bots sind deshalb nicht
              bot-spezifisch.
            </p>
          )}

          <section aria-labelledby="alerts-title" className="flex flex-col gap-1 rounded-lg border border-line bg-white px-5 py-[18px]">
            <h2 id="alerts-title" className="mb-2 text-base font-semibold">
              Alert-Regeln
            </h2>
            {ALERT_RULES.map((rule) => (
              <label key={rule.text} className="flex min-h-11 flex-wrap items-center gap-3 border-t border-neutral-bg text-sm">
                <input type="checkbox" checked readOnly disabled className="size-[18px] accent-accent" />
                <span className="min-w-[280px] flex-1">{rule.text}</span>
                <span className="text-[13px] text-muted">{rule.channel}</span>
              </label>
            ))}
            <p className="pt-2 text-xs text-muted">
              Der Versand per Slack und E-Mail kommt mit S20. Bis dahin zeigen die Kennzahlen oben, wenn sich die
              robots.txt ändert oder ein Bot neu blockiert ist.
            </p>
          </section>
        </>
      )}
    </div>
  );
}
